import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { editionOf, remainingFor, COLLECTOR } from "@/lib/shop";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EditionsContext = createContext(null);

// Live edition counters, driven by the Stripe webhook on the backend.
// Before the fetch resolves, falls back to the curated static numbers.
export function EditionsProvider({ children }) {
  const [soldMap, setSoldMap] = useState(null);

  const refresh = useCallback(() => {
    axios
      .get(`${API}/editions`)
      .then((r) => setSoldMap(r.data.counters || {}))
      .catch(() => setSoldMap(null));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const liveRemaining = useCallback(
    (productId, sizeId, colorId, tier = null) => {
      const total = tier === "collector" ? COLLECTOR.edition : editionOf(sizeId, colorId);
      if (!total) return 0;
      if (soldMap === null) {
        // Not loaded yet — curated static display value.
        return tier === "collector"
          ? Math.min(remainingFor(productId, "70x100", "noir"), COLLECTOR.edition)
          : remainingFor(productId, sizeId, colorId);
      }
      const key =
        tier === "collector"
          ? `${productId}:70x100:collector`
          : `${productId}:${sizeId}:${colorId}`;
      return Math.max(0, total - (soldMap[key] || 0));
    },
    [soldMap]
  );

  const liveEditionTotal = useCallback(
    (sizeId, colorId, tier = null) =>
      tier === "collector" ? COLLECTOR.edition : editionOf(sizeId, colorId),
    []
  );

  const liveAlmostGone = useCallback(
    (productId, sizeId, colorId, tier = null) => {
      const total = liveEditionTotal(sizeId, colorId, tier);
      const rem = liveRemaining(productId, sizeId, colorId, tier);
      return total > 0 && rem > 0 && rem / total < 0.2;
    },
    [liveEditionTotal, liveRemaining]
  );

  return (
    <EditionsContext.Provider
      value={{ liveRemaining, liveEditionTotal, liveAlmostGone, refresh, loaded: soldMap !== null }}
    >
      {children}
    </EditionsContext.Provider>
  );
}

export const useEditions = () => useContext(EditionsContext);
